import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, Bell, X, Check, AlertCircle, Loader2 } from "lucide-react";
import { useAuth } from "../../App";

const FreeTrialCard = ({ trial, onTrialCancelled }) => {
  const navigate = useNavigate();
  const { httpClient, validateToken } = useAuth();
  const [expanded, setExpanded] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [imageError, setImageError] = useState(false);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const daysRemaining = (endDate) => {
    if (!endDate) return 0;
    const today = new Date();
    const end = new Date(endDate);
    const diffTime = end - today;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const handleMerchantClick = () => {
    if (trial.merchant_id) {
      navigate(`/merchant/${trial.merchant_id}`);
    }
  };

  const handleCancelTrial = async () => {
    setIsCancelling(true);
    try {
      if (!httpClient) {
        throw new Error("Authentication client not available");
      }

      const response = await httpClient.request('/api/cancel-free-trial', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          merchant_id: trial.merchant_id,
          plan_id: trial.plan_id
        }),
      });

      if (response.status === 401) {
        const revalidated = await validateToken();
        if (!revalidated) {
          console.error("Authentication failed");
          return;
        }
        return handleCancelTrial();
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to cancel free trial');
      }

      if (onTrialCancelled) {
        onTrialCancelled();
      }
      
      setExpanded(false);
      
    } catch (error) {
      console.error('Cancel trial failed:', error);
      alert(`Cancellation failed: ${error.message}`);
    } finally {
      setIsCancelling(false);
    }
  };

  const daysLeft = daysRemaining(trial.end_of_trial);
  const isExpiringSoon = daysLeft <= 3 && daysLeft > 0;
  const isExpired = daysLeft <= 0;

  return (
    <div className="relative">
      {/* Main Free Trial Card */}
      <div
        className={`relative flex items-center justify-between p-4 rounded-xl transition-all duration-300 hover:shadow-md transform-gpu cursor-pointer ${
          expanded 
            ? "bg-gradient-to-br from-blue-50 to-purple-50 border-2 border-[#253985] shadow-xl" 
            : "bg-white border border-gray-200 hover:border-[#253985]/30 hover:shadow-md"
        }`}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="absolute inset-0 rounded-xl -z-10" />
        
        <div className="relative flex items-center space-x-4 flex-1 min-w-0">
          <div className="flex-shrink-0">
            {trial.logo && !imageError ? (
              <img
                src={trial.logo}
                alt={trial.merchant_name}
                className="h-10 w-10 rounded-lg object-contain bg-white border border-gray-200"
                onError={() => setImageError(true)}
              />
            ) : (
              <div className="h-10 w-10 rounded-lg bg-gray-200 flex items-center justify-center">
                <span className="text-sm font-medium text-gray-600">
                  {trial.merchant_name.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h4 className="font-medium text-gray-800 group-hover:text-[#253985] transition-colors truncate text-sm">
              {trial.merchant_name}
            </h4>
            <p className="text-xs text-gray-600 group-hover:text-[#253985]/80 transition-colors truncate">
              {trial.plan_name}
            </p>
          </div>
        </div>

        <div className="relative flex items-center space-x-3 ml-2">
          <div className="text-right">
            <div className="flex items-center justify-end">
              {isExpired ? (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  Expired
                </span>
              ) : isExpiringSoon ? (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                  <AlertCircle className="w-3 h-3 mr-1" />
                  {daysLeft}d
                </span>
              ) : (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  <Check className="w-3 w-3 mr-1" />
                  {daysLeft}d
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500 group-hover:text-[#253985]/70 transition-colors mt-1 hidden sm:block">
              Ends {formatDate(trial.end_of_trial)}
            </p>
          </div>

          <div className="text-right hidden sm:block min-w-[80px]">
            <p className="text-xs text-gray-700 group-hover:text-[#253985] transition-colors flex items-center">
              <Bell className="w-3 h-3 mr-1" />
              {trial.automatic_renewal ? 'Auto-renew' : 'No renewal'}
            </p>
          </div>
        </div>
      </div>

      {/* Expanded Details Section */}
      {expanded && (
        <div className="mt-1 p-4 bg-white border border-gray-200 rounded-lg shadow-xl transform transition-all duration-300 relative">
          <div className="grid grid-cols-1 gap-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-[#253985]">Plan Details</label>
              <div className="p-3 border border-gray-200 rounded-lg bg-gray-50">
                <p className="font-medium text-sm">{trial.plan_name}</p>
                <p className="text-xs text-gray-600 mt-1">
                  {trial.price > 0 
                    ? `Will convert to €${trial.price.toFixed(2)}/${trial.billing_interval || 'month'} after trial`
                    : 'Free trial'
                  }
                </p>
              </div>
            </div>
            
            <div className="space-y-2">
              <label className="block text-sm font-medium text-[#253985]">Trial Period</label>
              <div className="p-3 border border-gray-200 rounded-lg bg-gray-50 flex items-center">
                <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                <div>
                  <p className="text-sm">Ends on {formatDate(trial.end_of_trial)}</p>
                  <p className="text-xs text-gray-500">
                    {isExpired 
                      ? 'Trial has ended' 
                      : `${daysLeft} day${daysLeft !== 1 ? 's' : ''} remaining`
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-4 flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-3 sm:space-y-0">
            <div className="flex-1">
              <p className="text-xs text-gray-600">
                {trial.automatic_renewal 
                  ? 'This trial will automatically convert to a paid subscription'
                  : 'This trial will not automatically renew'
                }
              </p>
            </div>
            
            <div className="flex space-x-2">
              <button
                onClick={() => setExpanded(false)}
                className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
              
              {!isExpired && (
                <button
                  onClick={handleCancelTrial}
                  disabled={isCancelling}
                  className="px-3 py-1.5 text-xs bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isCancelling ? (
                    <>
                      <Loader2 className="h-3 w-3 animate-spin mr-1" />
                      Cancelling...
                    </>
                  ) : (
                    <>
                      <X className="h-3 w-3 mr-1" />
                      Cancel
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FreeTrialCard;